import { SmartCollectionInput } from 'api/product/getSmartCollection/types';
import { BasicPDEXInterface, BasicSegmentationInput } from 'api/product/types';
import {
  BreedSegmentationIds,
  NutritionSegmentationIds,
  SegmentationIds,
  isAgeSegmentation,
  isAllergySegmentation,
  isBreedSegmentation,
  isCatSegmentation,
  isNutritionSegmentation,
} from 'constants/segmentation';
import { SegmentationStore } from 'contexts/segmentation';
import { InputProductType } from 'interfaces/api-input/input-product-type';
import { PetType } from 'modules/collection/utils/modify-config/const';
import { PetProfile, isCatProfile, isDogProfile } from 'types/pet-profile';
import { normalizeProteinPreferences } from 'utils/pet-profile/protein-preferences';
import { omitNullableHandler } from 'utils/type-helper';
import { CollectionHandleKey } from 'config/collections/handles';
import { getAllHandles } from 'config/collections/utils/get-all-handles';
import { getSegmentationFromUrl } from 'utils/url/get-segmentation-from-url';

interface CdpConfig {
  url: Array<string>;
  productType: InputProductType;
  petType: PetType;
}

// Same shape as SegmentationData.redirectPages: a url -> meaning lookup lists every region.
const collectionPaths = (key: CollectionHandleKey): Array<string> => {
  return getAllHandles({ key }).map((handle) => {
    return `/collections/${handle}`;
  });
};

export const CDP_MAPPING: Array<CdpConfig> = [
  {
    url: collectionPaths('dogsWetFood'),
    productType: InputProductType.WetFood,
    petType: PetType.Dog,
  },
  {
    url: collectionPaths('dogsDryFood'),
    productType: InputProductType.DryFood,
    petType: PetType.Dog,
  },
  {
    url: collectionPaths('dogsBarf'),
    productType: InputProductType.Barf,
    petType: PetType.Dog,
  },
  {
    url: collectionPaths('dogsSnacks'),
    productType: InputProductType.Snacks,
    petType: PetType.Dog,
  },
  {
    url: collectionPaths('catsWetFood'),
    productType: InputProductType.WetFood,
    petType: PetType.Cat,
  },
  {
    url: collectionPaths('catsDryFood'),
    productType: InputProductType.DryFood,
    petType: PetType.Cat,
  },
  {
    url: collectionPaths('catsBarf'),
    productType: InputProductType.Barf,
    petType: PetType.Cat,
  },
  {
    url: collectionPaths('catsSnacks'),
    productType: InputProductType.Snacks,
    petType: PetType.Cat,
  },
];

export const getProductTypeFromCDPUrl = ({ url }: { url: string }): CdpConfig | undefined => {
  const trimmedUrl = url.replace(/\/collections\/[^/]+\/.*$/, '');

  return CDP_MAPPING.find(
    // It needs to remove the filters in the url, in case these are present
    (seg) => seg.url.some((redirectPage) => redirectPage === trimmedUrl)
  );
};

/**
 * Check if cdp is a generic cdp (e.g wet food, dry food )
 */
export const isGenericCdpPage = ({ url }: { url: string }): boolean => {
  return !!getProductTypeFromCDPUrl({ url });
};

const processSegmentation = (
  seg: SegmentationIds
): BasicSegmentationInput & BasicPDEXInterface => ({
  ...(isBreedSegmentation(seg) && seg !== BreedSegmentationIds.OtherBreed && { breed: seg }),
  ...(isAgeSegmentation(seg) && { age: seg }),
  ...(isNutritionSegmentation(seg) &&
    seg !== NutritionSegmentationIds.NoHelpNeeded &&
    seg !== NutritionSegmentationIds.Foodaid && { needs: [seg] }),
  ...(isAllergySegmentation(seg) && { allergies: [seg] }),
  pet_type: isCatSegmentation(seg) ? PetType.Cat : PetType.Dog,
});

/**
 * Generate api input from petProfile type
 * TODO: looks similar to toPayloadByPetProfile in src/contexts/pet-profile/utils/index.ts
 */
export const generateInputFromPetProfile = ({
  petProfile,
  includeDislikedProteins,
  omitFoodType = false,
}: {
  petProfile: PetProfile;
  includeDislikedProteins: boolean;
  omitFoodType?: boolean;
}): SmartCollectionInput => {
  const baseInput = generateInputFromSegmentation({
    petType: petProfile.petType,
    segmentation: {
      breedSegmentation: isDogProfile(petProfile) ? petProfile.selectedBreed : undefined,
      needSegmentation: petProfile.selectedNeeds,
      ageSegmentation: petProfile.selectedAge,
      allergySegmentation: petProfile.selectedAllergies,
    },
  });

  const proteinPreferences = isCatProfile(petProfile)
    ? normalizeProteinPreferences({
        liked: petProfile.selectedTastePreferences,
        disliked: petProfile.selectedDislikedProteins,
      })
    : undefined;

  const input: SmartCollectionInput = {
    ...baseInput,
    ...(omitFoodType ? {} : { food_types: petProfile.selectedFoodTypes }),
    ...(isCatProfile(petProfile) && {
      wet_food_tags: petProfile.selectedWetFoodTypes,
      protein_tags: proteinPreferences?.liked,
      ...(includeDislikedProteins && {
        disliked_protein_tags: proteinPreferences?.disliked,
      }),
    }),
  };

  if (!input.pet_type) {
    throw new Error('No pet type was determined after processing segmentations');
  }

  return input;
};

/** Keeps disliked-protein filtering on static fallback requests without applying preference ranking. */
export const generateFallbackInputFromPetProfile = ({
  petProfile,
  includeDislikedProteins,
  slug,
  foodTypes,
}: {
  petProfile: PetProfile;
  includeDislikedProteins: boolean;
  slug: string;
  foodTypes?: InputProductType[];
}): SmartCollectionInput => {
  const { disliked_protein_tags } = generateInputFromPetProfile({
    petProfile,
    includeDislikedProteins,
    omitFoodType: true,
  });

  return {
    slug,
    ...(foodTypes && { food_types: foodTypes }),
    ...(disliked_protein_tags && { disliked_protein_tags }),
  };
};

const keysToOmit: (keyof SegmentationStore)[] = ['lastSegmentation'];

/**
 * Generate api input from segmentation (single or multiple)
 */
export const generateInputFromSegmentation = ({
  petType,
  segmentation,
}: {
  petType?: PetType;
  segmentation: SegmentationIds | SegmentationStore;
}): BasicSegmentationInput & BasicPDEXInterface => {
  if (typeof segmentation === 'string') {
    if (isAllergySegmentation(segmentation)) {
      throw new Error('No pet type was determined after processing segmentations');
    }
    return processSegmentation(segmentation);
  }

  const values = Object.entries(segmentation)
    .map(([key, value]) => {
      if (!keysToOmit.includes(key as keyof SegmentationStore)) {
        return value;
      }
    })
    .flat()
    .filter(omitNullableHandler)
    .sort();

  let apiInput = {} as Partial<BasicSegmentationInput & BasicPDEXInterface>;

  for (const value of values) {
    const processed = processSegmentation(value);
    apiInput = mergeDynamicInput({
      target: apiInput,
      source: processed,
    });
  }

  const resolvedPetType = apiInput.pet_type ?? petType;
  if (!resolvedPetType) {
    throw new Error('No pet type was determined after processing segmentations');
  }
  apiInput.pet_type = resolvedPetType;

  return apiInput as BasicSegmentationInput & BasicPDEXInterface;
};

/**
 * Helper function to generate input from array of segmentationIds
 */
export const generateInputFromSegmentationIds = ({
  segmentationIds,
}: {
  segmentationIds: Array<SegmentationIds>;
}): BasicSegmentationInput & BasicPDEXInterface => {
  let apiInput = {} as Partial<BasicSegmentationInput & BasicPDEXInterface>;

  for (const value of segmentationIds) {
    const processed = processSegmentation(value);
    apiInput = mergeDynamicInput({
      target: apiInput,
      source: processed,
    });
  }
  return apiInput as BasicSegmentationInput & BasicPDEXInterface;
};

export const mergeDynamicInput = ({
  source,
  target,
}: {
  source: Partial<SmartCollectionInput>;
  target: Partial<SmartCollectionInput>;
}): SmartCollectionInput => {
  // Merge arrays rather than overwrite
  const mergedInput = {
    ...source,
    ...Object.keys(target).reduce((acc, key) => {
      const existingValue = source[key as keyof SmartCollectionInput];
      const newValue = target[key as keyof typeof target];

      if (Array.isArray(existingValue) && Array.isArray(newValue)) {
        // If both are arrays, concatenate them
        acc[key] = Array.from(new Set([...existingValue, ...newValue]));
      } else if (newValue !== undefined) {
        // Otherwise, just assign the new value
        acc[key] = newValue;
      }

      return acc;
    }, {} as Partial<SmartCollectionInput>),
    pet_type: source.pet_type || target.pet_type,
  } as SmartCollectionInput;

  // Check if pet type is set expect for slug input
  if (!mergedInput.pet_type && !('slug' in mergedInput)) {
    throw new Error('No pet type was determined after merging inputs');
  }

  return mergedInput;
};

/**
 * Helper function to generate SmartCollectionInput from segmentation
 */
export const generateInputFromCdpPath = ({
  path,
}: {
  path: string;
}): (BasicSegmentationInput & BasicPDEXInterface) | undefined => {
  const segmentation = getSegmentationFromUrl({
    path,
  });

  if (segmentation) {
    const { id } = segmentation;
    return processSegmentation(id);
  }

  const config = getProductTypeFromCDPUrl({ url: path });

  if (config) {
    return {
      food_types: [config.productType],
      pet_type: config.petType,
    };
  }
};
